import { IntlShape } from 'react-intl';

import { CatalogField, OPERATOR_MESSAGES } from './conditionForm';
import messages from './messages';
import { OPENING, SubjectType } from './types';

/**
 * The `Trigger` column's sentence: what makes a rule, or one of its levels,
 * apply (AXHE-4464 §2).
 *
 * A row used to say only how many levels a rule had, which answers "how much
 * sign-off" and never "when". "When" is the question an admin scans this list
 * for — whether a rule already covers the openings they are about to write
 * another one for — and it was only readable by opening the editor on every row
 * in turn.
 *
 * ## Why this reads the stored condition rather than a server-rendered string
 *
 * `Approvals::Condition` is the authority on what a condition means, and the
 * list endpoint ships the raw jsonb (`scope_conditions`, and `conditions` per
 * level). Asking the server to summarise as well would put the same vocabulary
 * in two places, and the operator names are already translated here — the
 * condition builder renders `greater_than` as "greater than" from the same map
 * this module imports.
 */

/** How many names a set-membership condition lists before the rest become a count. */
const NAMED_LIMIT = 2;

// `Approvals::Condition::SUPPORTED_TYPES`, less `data_key_based`, which is
// `conditionForm`'s to name because it is the one type this app also writes.
const ALWAYS = 'always';
const POSITION_BASED = 'position_based';
const LOCATION_BASED = 'location_based';
const OPENING_BASED = 'opening_based';
const ATTRIBUTE_BASED = 'attribute_based';
const COMPENSATION_BASED = 'compensation_based';

/**
 * One stored condition row, as far as this summary reads it.
 *
 * Every field is optional because the column is jsonb and the generated client
 * types a condition as `any` — so a row written by an older build, or by the API
 * directly, is a shape this has to survive rather than trust.
 */
export interface StoredTriggerCondition {
  type?: string;
  positions?: unknown[];
  locations?: unknown[];
  locationGroups?: unknown[];
  openings?: unknown[];
  dataKeyName?: string;
  dataKeyOperator?: string;
  dataKeyValue?: unknown;
  dataKeyCompareTo?: string;
  dataKeyCompareKey?: string;
  attrName?: string;
  attrOperator?: string;
  attrValue?: unknown;
  comparisonOperator?: string;
  useJobPayRange?: boolean;
  threshold?: unknown;
  rangeMin?: unknown;
  rangeMax?: unknown;
}

export interface TriggerSummaryInput {
  conditions?: unknown[] | null;
  /** `and` narrows the trigger, `or` widens it — so the joiner carries meaning. */
  logic?: string | null;
  /**
   * A rule with no conditions gates everything of its subject type; a level with
   * none applies whenever the rule matched (`ConditionEvaluator.level_outcome`
   * returns `:applies` on an empty set). Two different sentences, so the caller
   * says which it is rather than this inferring it from what it was handed.
   */
  scope: 'rule' | 'level';
  subjectType?: SubjectType | null;
  /**
   * The field catalog, so a data-key condition reads as "Sign-on bonus" rather
   * than `sign_on_bonus`. Empty while the catalog is in flight or failed — the
   * raw key is a worse label but still identifies the field, and a Trigger cell
   * must not go blank because an unrelated request has not landed.
   */
  fields: CatalogField[];
  intl: IntlShape;
}

/**
 * The nameable entries of a set-membership condition.
 *
 * Trimmed before the blank check, matching `.presence` on the server side: the
 * jsonb accepts `["", "  "]`, and a length test alone let a whitespace-only list
 * through as a named scope — "Position:" followed by two spaces.
 */
const asStrings = (raw: unknown[] | undefined) =>
  (raw ?? [])
    .map(entry => String(entry ?? '').trim())
    .filter(entry => entry.length > 0);

const asNumber = (raw: unknown) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * "Driver, Warehouse Associate +3" — the first two by name, the remainder as a
 * count.
 *
 * A cell that listed every position in an account-wide rule would be the tallest
 * thing in the table and still unreadable; two names is enough to recognise a
 * rule you wrote, and the editor is one click away for the rest.
 */
const namedList = (intl: IntlShape, names: string[], extra = 0) => {
  const shown = names.slice(0, NAMED_LIMIT);
  const remaining = names.length - shown.length + extra;
  const list = shown.join(', ');

  return remaining > 0
    ? intl.formatMessage(messages.triggerMore, { names: list, count: remaining })
    : list;
};

/** The operator as an admin reads it, falling back to the server's own token. */
const operatorLabel = (intl: IntlShape, operator: string | undefined) => {
  if (!operator) return undefined;
  const message = OPERATOR_MESSAGES[operator];
  return message ? intl.formatMessage(messages[message]) : operator;
};

/**
 * A stored operand as an admin reads it.
 *
 * Grouped when the catalog says the field holds a number, so the column does not
 * print "Sign-on bonus greater than 10000" beside "Compensation: over 50,000" —
 * one Trigger cell formatting numbers two ways was raised on review of #4599, and
 * `copywriting-voice` asks for commas for thousands.
 *
 * The catalog is what makes this possible and also what limits it: a `date` field
 * must not be grouped, and while the catalog is in flight there is no `type` to
 * read, so the value degrades to the raw stored string. That means the same
 * condition can render "10,000" or "10000" depending on whether an unrelated
 * request has landed — which is the existing behaviour of the label beside it
 * (`sign_on_bonus` before the catalog, "Sign-on bonus" after), and the same
 * trade: a legible cell now beats a blank one held for a fetch this row does not
 * otherwise need.
 */
const formatFieldValue = (
  value: unknown,
  field: CatalogField | undefined,
  intl: IntlShape,
) => {
  if (field?.type !== 'number') return String(value);
  const numeric = asNumber(value);
  // A non-numeric operand on a numeric field is a stored value the account has
  // since invalidated. Printed as it stands rather than swallowed: it is what the
  // rule actually holds, and `ConditionsValidator` is where it gets refused.
  return numeric === undefined ? String(value) : intl.formatNumber(numeric);
};

/**
 * A field condition — a customer-defined data key, or a standard attribute.
 *
 * Whether the operator takes a right-hand side is answered from the row itself
 * rather than from the catalog's `unary_operators`: `toStoredConditions` writes
 * neither operand for a unary operator, so an absent one *is* the answer, and it
 * stays right for any unary operator added server-side later. The catalog is not
 * always loaded here either, and a summary that needed it would print
 * "Cost Code is present 〈nothing〉" while it was in flight.
 */
const describeField = (
  condition: StoredTriggerCondition,
  { intl, fields }: TriggerSummaryInput,
) => {
  const isAttribute = condition.type === ATTRIBUTE_BASED;
  // `AttributeRegistry` keys are already display labels ("Base salary"), so only
  // a data key needs the catalog to become readable.
  const key = isAttribute ? condition.attrName : condition.dataKeyName;
  if (!key) return undefined;

  const field = isAttribute
    ? undefined
    : fields.find(candidate => candidate.key === key);
  const label = field?.label ?? key;
  const operator = operatorLabel(
    intl,
    isAttribute ? condition.attrOperator : condition.dataKeyOperator,
  );
  if (!operator) return label;

  const comparesToField =
    !isAttribute && condition.dataKeyCompareTo === 'dataKey';
  if (comparesToField) {
    const other = condition.dataKeyCompareKey;
    if (!other) return intl.formatMessage(messages.triggerFieldBare, { field: label, operator });
    const otherField = fields.find(candidate => candidate.key === other);
    return intl.formatMessage(messages.triggerFieldValue, {
      field: label,
      operator,
      value: otherField?.label ?? other,
    });
  }

  const value = isAttribute ? condition.attrValue : condition.dataKeyValue;
  if (value === undefined || value === null || value === '') {
    return intl.formatMessage(messages.triggerFieldBare, { field: label, operator });
  }

  return intl.formatMessage(messages.triggerFieldValue, {
    field: label,
    operator,
    value: formatFieldValue(value, field, intl),
  });
};


/**
 * A pay condition.
 *
 * The two halves read differently because they compare against different things:
 * with `useJobPayRange` the band is the opening's own posted range, so the
 * sentence names the range rather than a figure. Which bound each operator
 * reads is `ConditionEvaluator#readable_bounds` — `greater_than` compares
 * against the ceiling, `less_than` against the floor, `outside_of` against
 * either — so "above" and "below" are the accurate words, not "greater" and
 * "less" applied to a band.
 */
const describeCompensation = (
  condition: StoredTriggerCondition,
  { intl }: TriggerSummaryInput,
) => {
  const operator = condition.comparisonOperator;

  if (condition.useJobPayRange) {
    if (operator === 'greater_than') return intl.formatMessage(messages.triggerPayAboveRange);
    if (operator === 'less_than') return intl.formatMessage(messages.triggerPayBelowRange);
    if (operator === 'outside_of') return intl.formatMessage(messages.triggerPayOutsideRange);
    return undefined;
  }

  if (operator === 'outside_of') {
    const min = asNumber(condition.rangeMin);
    const max = asNumber(condition.rangeMax);
    if (min === undefined || max === undefined) return undefined;
    return intl.formatMessage(messages.triggerPayOutsideAmounts, {
      min: intl.formatNumber(min),
      max: intl.formatNumber(max),
    });
  }

  const threshold = asNumber(condition.threshold);
  if (threshold === undefined) return undefined;
  // No currency symbol: an offer's amount carries neither a currency nor a pay
  // period (AXHE-4063), so "$10,000" would be a unit this surface cannot know.
  const amount = intl.formatNumber(threshold);
  if (operator === 'greater_than') return intl.formatMessage(messages.triggerPayOver, { amount });
  if (operator === 'less_than') return intl.formatMessage(messages.triggerPayUnder, { amount });
  return undefined;
};

/**
 * One condition as a phrase, or `undefined` when this build cannot describe it.
 *
 * Undescribable is deliberately silent rather than a placeholder: the summary is
 * a scanning aid, and "Unknown condition" in the Trigger column of a live
 * compliance gate reads as a fault in the rule. The count of conditions the
 * summary dropped is what the caller surfaces instead.
 */
const describeCondition = (
  condition: StoredTriggerCondition,
  input: TriggerSummaryInput,
): string | undefined => {
  const { intl } = input;

  switch (condition.type) {
    // No `ALWAYS` case: `triggerSummary` filters those rows out before it gets
    // here, because a row that restricts nothing is not part of what a trigger
    // says. The word "Always" is still the phrase for a set that restricts
    // nothing *as a whole* — see `unrestricted`.
    case POSITION_BASED: {
      const positions = asStrings(condition.positions);
      return positions.length > 0
        ? intl.formatMessage(messages.triggerPosition, {
            list: namedList(intl, positions),
          })
        : undefined;
    }

    case LOCATION_BASED: {
      const locations = asStrings(condition.locations);
      // Location *groups* are stored as `LocationGroup#external_id` rather than
      // by name (`Approvals::Condition#location_groups` says why), and the list
      // endpoint ships the ids raw — so a group can be counted here but not
      // named. Printing a UUID in a summary column would be worse than the
      // count, and folding groups into the overflow keeps a mixed condition
      // honest about its size.
      const groups = asStrings(condition.locationGroups);
      if (locations.length > 0) {
        return intl.formatMessage(messages.triggerLocation, {
          list: namedList(intl, locations, groups.length),
        });
      }
      return groups.length > 0
        ? intl.formatMessage(messages.triggerLocationGroups, { count: groups.length })
        : undefined;
    }

    case OPENING_BASED: {
      // Openings are funnel external_ids for the same reason, so this one is a
      // count outright — there is no nameable half to lead with.
      const openings = asStrings(condition.openings);
      return openings.length > 0
        ? intl.formatMessage(messages.triggerOpenings, { count: openings.length })
        : undefined;
    }

    case COMPENSATION_BASED:
      return describeCompensation(condition, input);

    case ATTRIBUTE_BASED:
      return describeField(condition, input);

    default:
      // `data_key_based`, and nothing else: `Condition#supported?` rejects an
      // unknown type at save, so anything reaching here without a describable
      // shape is a row this build predates.
      return describeField(condition, input);
  }
};

export interface TriggerSummary {
  /**
   * What to render. Never empty, and it admits its own gaps — a set this build
   * only partly understands is summarised as far as it goes and then says how
   * many conditions it left out.
   */
  text: string;
  /**
   * Conditions this build could not put into words. Zero on every rule the
   * current editor can write; non-zero means the row is showing less than the
   * rule does, which the cell has to admit rather than imply completeness.
   */
  omitted: number;
  /**
   * True when this scope places no restriction of its own — the empty-set case,
   * which reads "All offers"/"All openings" for a rule and "Always" for a level.
   * The single-level row uses this to fold a rule's scope and its one level's
   * conditions into one cell without stacking a no-op half onto the phrase that
   * actually gates: an empty scope adds nothing to a level condition, and an
   * empty level adds nothing to a scope. Two of these joined would read
   * "All offers and Position: Driver", which overstates the scope and buries the
   * one condition that decides whether the gate fires.
   */
  gatesEverything: boolean;
  /**
   * True when `text` is an `or` of two or more described conditions — a widening
   * disjunction whose own joiner binds looser than the `and` that
   * `combineTriggers` folds a single-level rule's two halves together with.
   *
   * Left flat, `(Position or Location) and Compensation` prints as
   * "Position: Driver or Location: Dallas and Compensation: over 50,000", which
   * reads as `Position or (Location and Compensation)` — the wrong grouping, and
   * a broader gate than the rule holds. `combineTriggers` reads this to bracket
   * such a half before the join so the disjunction stays contained. A single
   * condition, or an `and`-joined list, is already unambiguous inside an outer
   * `and` (conjunction is associative) and never sets this.
   */
  disjunction: boolean;
}

export const triggerSummary = (input: TriggerSummaryInput): TriggerSummary => {
  const { conditions, logic, scope, subjectType, intl } = input;
  const rows = (conditions ?? []).filter(
    (row): row is StoredTriggerCondition => typeof row === 'object' && row !== null,
  );

  // What this set says when it restricts nothing. A rule gates its whole subject
  // type; a level applies whenever the rule matched.
  const unrestricted = (): TriggerSummary => ({
    text: intl.formatMessage(
      scope === 'level'
        ? messages.triggerAlways
        : subjectType === OPENING
        ? messages.triggerAllOpenings
        : messages.triggerAllOffers,
    ),
    omitted: 0,
    gatesEverything: true,
    disjunction: false,
  });

  // `always` places no restriction: `ConditionEvaluator#evaluate_state` answers
  // it `:match` unconditionally, so `[{ always }]` is the second of the two
  // encodings of "applies to everything" — `[]` being the first, and both live.
  // Rendered as a condition it read "Always and Position: Driver", which states a
  // narrowing this rule does not have and buries the half that gates
  // (#4599 review).
  //
  // Which rows survive depends on the joiner, and the `or` case is the one that
  // matters: an `always` row inside a disjunction satisfies the whole set, so
  // `[always, data_key]` under `or` gates everything however narrow the other row
  // looks. Under `and` an `always` row simply adds nothing and drops out.
  const restricting = rows.filter(row => row.type !== ALWAYS);
  const alwaysCount = rows.length - restricting.length;

  if (rows.length === 0 || restricting.length === 0) return unrestricted();
  if (logic === 'or' && alwaysCount > 0) return unrestricted();

  const parts = restricting.map(row => describeCondition(row, input));
  const described = parts.filter((part): part is string => Boolean(part));
  const omitted = parts.length - described.length;

  if (described.length === 0) {
    return {
      text: intl.formatMessage(messages.triggerUnnamedConditions, { count: omitted }),
      omitted,
      gatesEverything: false,
      disjunction: false,
    };
  }

  // `formatList` rather than a joined string: "and"/"or" placement is a locale
  // decision, and this is exactly the case `Intl.ListFormat` exists for. Native
  // in every browser this app supports; `i18n.polyfills.js` additionally loads
  // locale data for en, es, fr and de, and formatjs degrades rather than throws
  // for a locale it has none for.
  const isOr = logic === 'or';
  const summary = intl.formatList(described, {
    type: isOr ? 'disjunction' : 'conjunction',
  });

  return {
    text: summary,
    omitted,
    gatesEverything: false,
    // A single described condition prints no joiner at all, so it cannot be
    // misgrouped inside an outer `and` — only an `or` of two or more widens the
    // phrase in a way the fold has to bracket.
    disjunction: isOr && described.length > 1,
  };
};

/**
 * A half's text, parenthesised when it is a disjunction so the `and` folding it
 * onto the other half cannot rebind its looser `or`. Left as-is otherwise: a
 * single condition or an `and` half is already unambiguous inside a conjunction.
 */
const bracketDisjunction = (trigger: TriggerSummary, intl: IntlShape) =>
  trigger.disjunction
    ? intl.formatMessage(messages.triggerGroup, { group: trigger.text })
    : trigger.text;

/**
 * The Trigger cell for a single-level rule rendered inline (AXHE-4464).
 *
 * A single-level rule has two independent gates and only one row to say so: the
 * rule's own scope conditions decide whether the rule applies at all, and its
 * one level's conditions decide whether that level — the rule's only step —
 * runs. `RuleMatcher` requires the scope to match AND
 * `ConditionEvaluator#level_outcome` to be `:applies`, so the rule's real
 * trigger is the conjunction of the two.
 *
 * A multi-level rule spends a row per level and has no need of this — each level
 * carries its own trigger below the scope. But a single-level rule shows only
 * the scope inline, and a scope that reads "All offers" while the level narrows
 * it to a position hid the one condition that decides whether the gate fires:
 * the row said the rule caught every offer when it caught almost none. Folding
 * both halves in is what keeps the inline row honest about what the rule does.
 *
 * The no-op half drops out rather than stacking: an empty scope
 * ("All offers"/"All openings") adds nothing to a level condition, and an empty
 * level ("Always") adds nothing to a scope — joining either in would overstate
 * the trigger. When both gate everything, the scope's own "All offers" is the
 * accurate answer and stands alone.
 *
 * The fold is an `and`, but each half combines its own conditions with an `or`
 * or an `and` of its own. An `or` half binds looser than the fold's `and`, so
 * joining it flat — "Position: Driver or Location: Dallas and Compensation: over
 * 50,000" — reads as `Position or (Location and Compensation)`, a wider gate
 * than `(Position or Location) and Compensation` the rule actually holds. Such a
 * half is bracketed before the join so its disjunction stays contained; a single
 * condition or an `and` half needs none, since conjunction is associative.
 */
/**
 * A summary as a cell renders it: the phrase, plus what it had to leave out.
 *
 * The "+N more conditions" suffix is appended here rather than baked into
 * `text`, because a single-level rule's cell folds two summaries into one
 * (`combineTriggers`) and each half carrying its own suffix said it twice —
 * "Position: Driver +1 more condition and Cost Code is present +1 more
 * condition". Applied once to the summed count it says "+2 more conditions", and
 * that sum is what `combineTriggers` was already keeping and nothing read
 * (#4599 review).
 *
 * A trigger has to admit its own gaps: summarising the half this build
 * understands and silently dropping the rest reads as a complete trigger, which
 * on a compliance gate is the wrong way to be wrong.
 */
export const triggerText = (summary: TriggerSummary, intl: IntlShape) =>
  summary.omitted > 0
    ? intl.formatMessage(messages.triggerAndMore, {
        summary: summary.text,
        count: summary.omitted,
      })
    : summary.text;

export const combineTriggers = (
  scopeTrigger: TriggerSummary,
  levelTrigger: TriggerSummary,
  intl: IntlShape,
): TriggerSummary => {
  const omitted = scopeTrigger.omitted + levelTrigger.omitted;
  const gatesEverything = scopeTrigger.gatesEverything && levelTrigger.gatesEverything;

  // Whichever half actually narrows. Both no-ops leaves the scope's phrase,
  // which is the widest true thing this cell can say ("All offers"). The
  // surviving half stands alone, so its own grouping is carried through
  // unchanged — nothing is joined onto it here.
  if (scopeTrigger.gatesEverything && levelTrigger.gatesEverything) {
    return {
      text: scopeTrigger.text,
      omitted,
      gatesEverything,
      disjunction: scopeTrigger.disjunction,
    };
  }
  if (scopeTrigger.gatesEverything) {
    return {
      text: levelTrigger.text,
      omitted,
      gatesEverything,
      disjunction: levelTrigger.disjunction,
    };
  }
  if (levelTrigger.gatesEverything) {
    return {
      text: scopeTrigger.text,
      omitted,
      gatesEverything,
      disjunction: scopeTrigger.disjunction,
    };
  }

  // Both narrow, so the rule fires only where both hold — a conjunction, always,
  // regardless of the `or` each half may combine its own conditions with. A half
  // that is itself an `or` of several conditions is bracketed first so the flat
  // "and" cannot rebind it; same `formatList` the summary uses, so "and"
  // placement stays a locale decision.
  return {
    text: intl.formatList(
      [bracketDisjunction(scopeTrigger, intl), bracketDisjunction(levelTrigger, intl)],
      { type: 'conjunction' },
    ),
    omitted,
    gatesEverything,
    // The fold's own joiner is the `and`; the result is a conjunction whatever
    // its bracketed halves were.
    disjunction: false,
  };
};
